import { addDays } from "date-fns";
import React, { useState } from "react";
import { Redirect } from "react-router-dom";
import styled from "styled-components";
import { QueryParameters, Routes } from "@lifeat/shared/system/RouteConstants";
import { formatDateForDateInput } from "@lifeat/shared/util/DateUtil";
import { LifeAtButtonV2 } from "@common/LifeAtButtonV2/LifeAtButtonV2";
import LifeAtProgressBar from "@common/LifeAtProgressBar/LifeAtProgressBar";
import { useUserContext } from "@contexts/UserContext";
import { useEvent } from "@hooks/useEvent";
import { buildRoute } from "@util/RouteUtil";
import { BoardViewFilterService } from "../../features/task-management/BoardViewFilterService";
import { TaskManagementService } from "../../features/task-management/TaskManagementService";
import { getDraftTaskUID } from "../../features/task-management/TaskManagementUtil";
import useTaskData from "../../features/task-management/useTaskData";

const Root = styled.div``;
const Content = styled.main`
    display: flex;
    flex-direction: column;
`;

export default function StressTestPage() {
    const { user } = useUserContext();
    const { taskItems$, tags } = useTaskData({
        uid: user?.uid,
        filterService: BoardViewFilterService(user?.uid),
    });
    const [dateStart, setDateStart] = useState(formatDateForDateInput(addDays(new Date(), -7)));
    const [dateEnd, setDateEnd] = useState(formatDateForDateInput(addDays(new Date(), 7)));
    const [totalOperations, setTotalOperations] = useState(0);
    const [totalOperationsCompleted, setTotalOperationsCompleted] = useState(0);
    const lock = totalOperationsCompleted !== totalOperations;

    const createThousandTasks = useEvent(async () => {
        if (lock) return;
        setTotalOperations(100);
        setTotalOperationsCompleted(0);
        for (let i = 0; i < 100; i++) {
            TaskManagementService.createNewTask({
                clientId: getDraftTaskUID(),
                title: `Task ${i}`,
            }).then(() => setTotalOperationsCompleted((before) => before + 1));
        }
    });

    const deleteAllTasks = useEvent(async () => {
        if (lock) return;
        setTotalOperations(1);
        setTotalOperationsCompleted(0);
        await TaskManagementService.deleteTasks(Object.keys(taskItems$.value));
        setTotalOperationsCompleted(1);
    });

    const assignRandomTag = useEvent(async () => {
        if (lock) return;
        const taskIds = Object.keys(taskItems$.value);
        const tagIds = tags.map((tag) => tag.id);
        setTotalOperations(taskIds.length);
        setTotalOperationsCompleted(0);
        for (const taskId of taskIds) {
            const randomTagId = tagIds[Math.floor(Math.random() * tagIds.length)];
            TaskManagementService.updateTaskTag(taskId, randomTagId).then(() =>
                setTotalOperationsCompleted((before) => before + 1),
            );
        }
    });

    const assignRandomTime = useEvent(async () => {
        if (lock) return;
        const taskIds = Object.keys(taskItems$.value);
        setTotalOperations(taskIds.length);
        setTotalOperationsCompleted(0);
        for (const taskId of taskIds) {
            const randomTime = randomDateInRange(dateStart, dateEnd);
            TaskManagementService.updateTaskScheduleTime(taskId, randomTime.toISOString()).then(() =>
                setTotalOperationsCompleted((before) => before + 1),
            );
        }
    });

    const canAccessStressTestPage = process.env.NODE_ENV === "development";

    if (canAccessStressTestPage == null) {
        return <div>Checking if you have authority...</div>;
    }
    if (canAccessStressTestPage === false) {
        return <Redirect to={buildRoute(Routes.App)} />;
    }

    return (
        <Root>
            <Content>
                <h3>Stress Test Dashboard</h3>
                {user ? (
                    <>
                        <p>{tags.length} tags</p>
                        <p>{Object.keys(taskItems$.value).length} tasks</p>
                        <LifeAtButtonV2 variant="outline" onClick={createThousandTasks} isDisabled={lock}>
                            Create 100 Tasks
                        </LifeAtButtonV2>
                        <LifeAtButtonV2 variant="outline" onClick={deleteAllTasks} isDisabled={lock}>
                            Delete All Tasks
                        </LifeAtButtonV2>
                        <LifeAtButtonV2 variant="outline" onClick={assignRandomTag} isDisabled={lock}>
                            Assign Random Tag to All Tasks
                        </LifeAtButtonV2>
                        <label>Random start date</label>
                        <input type="date" value={dateStart} onChange={(e) => setDateStart(e.target.value)} />
                        <label>Random end date</label>
                        <input type="date" value={dateEnd} onChange={(e) => setDateEnd(e.target.value)} />
                        <LifeAtButtonV2 variant="outline" onClick={assignRandomTime} isDisabled={lock}>
                            Assign Random Time to All Tasks
                        </LifeAtButtonV2>
                    </>
                ) : (
                    <>
                        <LifeAtButtonV2
                            variant="outline"
                            href={`/${Routes.DesktopMain}?${QueryParameters.RedirectSuccess}=${encodeURIComponent(
                                "/stress-test",
                            )}`}
                        >
                            Log in
                        </LifeAtButtonV2>
                    </>
                )}
                <LifeAtProgressBar
                    progressPercentage={(totalOperationsCompleted / totalOperations) * 100}
                    progressTotal={totalOperations}
                    progressNum={totalOperationsCompleted}
                />
            </Content>
        </Root>
    );
}

function randomDateInRange(start: string, end: string): Date {
    const dateStart = new Date(start);
    const dateEnd = new Date(end);
    const randomTime = dateStart.getTime() + Math.random() * (dateEnd.getTime() - dateStart.getTime());
    return new Date(randomTime);
}
